<script setup>
    // Vue
    import { ref, defineEmits, defineExpose, inject } from 'vue';

    // Store
    import { useCounselorProfileStore } from '@/stores/modal/counselorProfileStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    const languageStore = useLanguageStore();

    // ----------------------------
    // カウンセラープロフィール
    // ----------------------------
    const counselorProfileStore = useCounselorProfileStore();

    /**
     * カウンセラープロフィール取得処理
     * @param API_URL
     * @param queryParam
     * @returns true:正常 false:異常
     */
    async function getCounselorProfile(API_URL, queryParam) {
        // カウンセラー情報取得APIをコールする。
        const response = await counselorProfileStore.getCounselorProfileAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
        return true;
    }

    /**
     * カウンセラープロフィール画像取得処理
     * @param API_URL
     * @param queryParam
     * @returns true:正常 false:異常
     */
    async function getCounselorImg(API_URL, queryParam) {
        // カウンセラー写真取得APIをコールする。
        const response = await counselorProfileStore.getCounselorProfileImageAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
        return true;
    }

    // ----------------------------
    // 親側実装必須処理
    // ----------------------------
    const emit = defineEmits(['counselorProfileModal:close']);
    function close() {
        // Storeを初期化する。
        counselorProfileStore.reset();

        // 親画面のクローズ処理を呼び出す
        emit('counselorProfileModal:close');
    }

    /**
     * 親画面からカウンセラープロフィールモーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @param {String} counselorPk カウンセラーPK
     * @returns なし
     */
    async function openModal(trg, counselorPk) {
        // Storeを初期化する。
        counselorProfileStore.reset();

        // APIのURLを引き継ぐ
        API_URL.value = trg;

        // カウンセラー情報取得APIのクエリパラメーターを生成する。
        const counselorProfileQueryParam = {
            Lang: languageStore.getLanguageValue,
            CounselorPk: counselorPk,
        };

        // カウンセラー情報取得APIをコールする。
        const result = await getCounselorProfile(API_URL.value, counselorProfileQueryParam);

        if (!result) {
            return;
        }

        // カウンセラー写真取得APIのクエリパラメーターを生成する。
        const counselorProfileImgQueryParam = {
            CounselorPk: counselorPk,
        };

        // カウンセラー写真取得APIをコールする。
        await getCounselorImg(API_URL.value, counselorProfileImgQueryParam);

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalCounselorDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }
    defineExpose({
        openModal,
    });
</script>

<style></style>

<template>
    <article id="modalCounselorDetail" class="modal" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ $t('counselorProfile.parts.modalTitle1') }}</h3>
        <section class="modalContents">
            <div class="modalContentsBox">
                <div class="splitBlock">
                    <ul class="leftBlock">
                        <li>
                            <!--カウンセラー写真-->
                            <template v-if="!Utils.isNullEmpty(counselorProfileStore.imgFileExt)">
                                <img :src="'data:image/' + counselorProfileStore.imgFileExt + ';base64,' + counselorProfileStore.counselorPhotoImg" />
                            </template>
                        </li>
                        <li>
                            <!--カウンセラー名-->
                            <span>{{ counselorProfileStore.counselorNm }}</span>
                        </li>
                    </ul>
                    <ul class="rightBlock">
                        <li>
                            <dl>
                                <!--学歴-->
                                <dt>{{ $t('counselorProfile.parts.dl1') }}</dt>
                                <dd>
                                    <p>{{ counselorProfileStore.counselorAcademy }}</p>
                                </dd>
                            </dl>
                        </li>
                        <li>
                            <dl>
                                <!--経験-->
                                <dt>{{ $t('counselorProfile.parts.dl2') }}</dt>
                                <dd>
                                    <p>{{ counselorProfileStore.counselorExperience }}</p>
                                </dd>
                            </dl>
                        </li>
                        <li>
                            <dl>
                                <!--資格-->
                                <dt>{{ $t('counselorProfile.parts.dl3') }}</dt>
                                <dd>
                                    <p>{{ counselorProfileStore.counselorQualification }}</p>
                                </dd>
                            </dl>
                        </li>
                        <li>
                            <dl>
                                <!--得意分野-->
                                <dt>{{ $t('counselorProfile.parts.dl4') }}</dt>
                                <dd>
                                    <p>{{ counselorProfileStore.counselorField }}</p>
                                </dd>
                            </dl>
                        </li>
                        <li>
                            <dl>
                                <!--コメント-->
                                <dt>{{ $t('counselorProfile.parts.dl5') }}</dt>
                                <dd>
                                    <p>{{ counselorProfileStore.counselorComment }}</p>
                                </dd>
                            </dl>
                        </li>
                    </ul>
                </div>
            </div>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--閉じる-->
                    <button type="button" class="btnNormal" @click="close">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
